import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { showToast } from "../helper/notifications";
import api from "../services/api";
import PageHeader from "../components/common/PageHeader";
import FloatingFormActions from "../components/common/FloatingFormActions";

const AuthorizeUserPage = () => {
  const { id } = useParams(); // Check if we are editing
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [initialData, setInitialData] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "editor",
  });

  const hasFormChanged = () => {
    if (!initialData) {
      return Boolean(formData.name || formData.email || formData.role !== "editor");
    }
    return (
      (formData.name || "") !== (initialData.name || "") ||
      (formData.email || "") !== (initialData.email || "") ||
      (formData.role || "") !== (initialData.role || "")
    );
  };

  // Fetch data only if in Edit Mode
  useEffect(() => {
    if (id) {
      const fetchUser = async () => {
        setFetching(true);
        try {
          const res = await api.get(`/users/${id}`);
          const userData = res.data?.user || res.data;
          const loadedObj = {
            name: userData.name || "",
            email: userData.email || "",
            role: userData.role || "editor",
          };
          setFormData(loadedObj);
          setInitialData(loadedObj);
        } catch (error) {
          showToast("error", "Failed to load user data");
          navigate("/users");
        } finally {
          setFetching(false);
        }
      };
      fetchUser();
    }
  }, [id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        // Edit existing user
        await api.put(`/users/${id}`, formData);
        showToast("success", "User updated successfully");
      } else {
        await api.post("/users", formData); 
        showToast("success", "Email authorized successfully");
      }
      navigate("/users");
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Operation failed";
      showToast("error", errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="d-flex justify-content-center py-80">
        <Icon icon="line-md:loading-twotone-loop" className="text-4xl text-primary-600" />
      </div>
    );
  }

  return (
    <>
      <PageHeader 
        title={id ? "Edit User Access" : "Authorize Email"} 
        subtitle="User Management" 
      />

      <div className="card radius-12 border-0 shadow-none bg-base">
        <div className="card-body p-24">
          <div className="mb-24">
            <h6 className="mb-4 text-lg fw-bold text-primary-light">
              {id ? "Update Permissions" : "New Access Authorization"}
            </h6>
            <p className="text-sm text-secondary-light">
              {id ? "Modify the name, email, or role for this user." : "Authorized users will be able to log in using this email address."}
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="form-has-floating-actions">
            <div className="row gy-4">
              <div className="col-md-6">
                <label className="form-label fw-semibold text-secondary-light">Full Name</label>
                <input 
                  type="text" 
                  className="form-control radius-8" 
                  placeholder="Enter full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required 
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold text-secondary-light">Email Address</label>
                <div className="icon-field">
                  <input 
                    type="email" 
                    className="form-control radius-8" 
                    placeholder="name@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required 
                  />
                  <span className="icon">
                    <Icon icon="mage:email" />
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold text-secondary-light">Assign Role</label>
                <select 
                  className="form-select radius-8"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="admin">Administrator</option>
                  <option value="editor">Editor</option>
                </select>
              </div>

            </div>

            {/* STICKY BOTTOM ACTION BAR */}
            <FloatingFormActions
              isDirty={hasFormChanged()}
              cancelUrl="/users"
              cancelText="Discard"
              saveText={id ? "Save Changes" : "Authorize Access"}
              loading={loading}
              saveIcon="solar:check-read-linear"
            />
          </form>
        </div>
      </div>
    </>
  );
};

export default AuthorizeUserPage;